import React from 'react';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from 'components/ui/dialog';
import { CheckCircle, MessageSquare, Search, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface WelcomeDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onGetStarted?: () => void;
}

export function WelcomeDialog({ open, onOpenChange, onGetStarted }: WelcomeDialogProps) {
    const { t } = useTranslation();

    const handleGetStarted = () => {
        onOpenChange(false);
        onGetStarted?.();
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md mx-4 rounded-lg" hideClose={true}>
                {/* Header */}
                <DialogHeader className="relative">
                    <div className="flex items-center justify-center gap-3 pr-8">
                        <div className="bg-green-100 dark:bg-green-900/20 p-2 rounded-full">
                            <CheckCircle className="text-green-600 dark:text-green-400" size={24} />
                        </div>
                        <DialogTitle className="text-xl font-semibold text-foreground">
                            {t('WELCOME_TO_EAGLEGPT')}
                        </DialogTitle>
                    </div>
                </DialogHeader>

                <div className="space-y-6 pt-4">
                    {/* Welcome message */}
                    <div className="space-y-3">
                        <p className="text-base text-foreground leading-relaxed">
                            🎉 {t('YOUR_FREE_TRIAL_IS')}
                        </p>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                            {t('HERE_WHAT_YOU_CAN_DO')}
                        </p>
                    </div>

                    {/* Features list */}
                    <div className="space-y-4">
                        <div className="flex items-start gap-3">
                            <div className="bg-blue-100 dark:bg-blue-900/20 p-1.5 rounded-md mt-0.5">
                                <MessageSquare className="text-blue-600 dark:text-blue-400" size={16} />
                            </div>
                            <div>
                                <h4 className="font-medium text-foreground text-sm">{t('SMART_CONVERSATIONS')}</h4>
                                <p className="text-xs text-muted-foreground">{t('CHAT_NATURALLY_WITH_AI')}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <div className="bg-purple-100 dark:bg-purple-900/20 p-1.5 rounded-md mt-0.5">
                                <Search className="text-purple-600 dark:text-purple-400" size={16} />
                            </div>
                            <div>
                                <h4 className="font-medium text-foreground text-sm">{t('WEB_SEARCH_INTEGRATION')}</h4>
                                <p className="text-xs text-muted-foreground">{t('GET_UP_TO_DATE_INFORMATION')}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <div className="bg-orange-100 dark:bg-orange-900/20 p-1.5 rounded-md mt-0.5">
                                <Zap className="text-orange-600 dark:text-orange-400" size={16} />
                            </div>
                            <div>
                                <h4 className="font-medium text-foreground text-sm">{t('LIGHTINING_FAST_RESPONSE')}</h4>
                                <p className="text-xs text-muted-foreground">{t('EXPERIENCE_QUICK_INTELLIGENT')}</p>
                            </div>
                        </div>
                    </div>

                    {/* Call to action */}
                    <div className="bg-muted/50 p-4 rounded-lg">
                        <p className="text-sm font-medium text-foreground mb-2">{t('READY_TO_GET_STARTED')}</p>
                        <p className="text-xs text-muted-foreground">
                            {t('TRY_ASKING_ME_ANYTHING')}
                        </p>
                    </div>
                </div>

                {/* Footer with Let's go button */}
                <DialogFooter>
                    <Button
                        onClick={handleGetStarted}
                        className="w-full bg-primary text-white font-medium"
                    >
                        {t('LETS_GO')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
